import { useState, useEffect, useCallback } from 'react';
import { referenceDataSyncService } from '../services/referenceDataSyncService';
import type { SyncStatus } from '../types/referenceData';

export interface UseReferenceDataOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export interface ReferenceDataState {
  establishments: any[];
  locations: any[];
  productCategories: any[];
  syncStatus: SyncStatus[];
  isLoading: boolean;
  lastUpdated: number | null;
  error: string | null;
}

export function useReferenceData(options: UseReferenceDataOptions = {}) {
  const [state, setState] = useState<ReferenceDataState>({
    establishments: [],
    locations: [],
    productCategories: [],
    syncStatus: [],
    isLoading: true,
    lastUpdated: null,
    error: null,
  });

  const { autoRefresh = true, refreshInterval = 300000 } = options; // 5 minutes default

  const loadData = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const [establishments, locations, productCategories, syncStatus] = await Promise.all([
        referenceDataSyncService.getEstablishments(),
        referenceDataSyncService.getLocations(),
        referenceDataSyncService.getProductCategories(),
        referenceDataSyncService.getSyncStatus(),
      ]);

      setState({
        establishments,
        locations,
        productCategories,
        syncStatus,
        isLoading: false,
        lastUpdated: Date.now(),
        error: null,
      });
    } catch (error) {
      console.error('Failed to load reference data:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
    }
  }, []);

  const syncAllData = useCallback(async () => {
    if (!navigator.onLine) {
      return await referenceDataSyncService.getSyncStatus();
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const syncResults = await referenceDataSyncService.syncAllEntities();

      // Reload data after sync
      await loadData();

      return syncResults;
    } catch (error) {
      console.error('Failed to sync reference data:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Sync failed',
      }));
      throw error;
    }
  }, [loadData]);

  const syncEntity = useCallback(
    async (entity: string) => {
      if (!navigator.onLine) {
        return await referenceDataSyncService.forceSyncEntity(entity); // Will return cached data info
      }

      try {
        const result = await referenceDataSyncService.forceSyncEntity(entity);
        await loadData(); // Reload data after sync
        return result;
      } catch (error) {
        console.error(`Failed to sync ${entity}:`, error);
        throw error;
      }
    },
    [loadData]
  );

  const getLocationsByDistrict = useCallback(
    (district: string) => {
      return state.locations.filter((location) => location.district === district);
    },
    [state.locations]
  );

  const getCategoriesByMainCategory = useCallback(
    (mainCategory: string) => {
      return state.productCategories.filter((category) => category.mainCategory === mainCategory);
    },
    [state.productCategories]
  );

  const getUniqueDistricts = useCallback(() => {
    const districts = state.locations.map((location) => location.district);
    return [...new Set(districts)].sort();
  }, [state.locations]);

  const getUniqueMainCategories = useCallback(() => {
    const mainCategories = state.productCategories.map((category) => category.mainCategory);
    return [...new Set(mainCategories)].sort();
  }, [state.productCategories]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (navigator.onLine) {
        loadData();
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loadData]);

  // Online/offline handling
  useEffect(() => {
    const handleOnline = () => {
      loadData();
    };

    const handleOffline = () => {};

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [loadData]);

  return {
    ...state,

    // Actions
    refresh: loadData,
    syncAll: syncAllData,
    syncEntity,

    // Helpers
    getLocationsByDistrict,
    getCategoriesByMainCategory,
    getUniqueDistricts,
    getUniqueMainCategories,

    // Status checkers
    isOnline: navigator.onLine,
    hasData:
      state.establishments.length > 0 ||
      state.locations.length > 0 ||
      state.productCategories.length > 0,
    isAllSynced: state.syncStatus.every((status) => status.isSync),
  };
}

// Hook for individual entity data
export function useEstablishments() {
  const [establishments, setEstablishments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadEstablishments = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await referenceDataSyncService.getEstablishments();
        setEstablishments(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load establishments');
      } finally {
        setIsLoading(false);
      }
    };

    loadEstablishments();
  }, []);

  return { establishments, isLoading, error };
}

export function useLocations(district?: string) {
  const [locations, setLocations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setIsLoading(true);
        setError(null);
        let data;
        if (district) {
          data = await referenceDataSyncService.getLocationsByDistrict(district);
        } else {
          data = await referenceDataSyncService.getLocations();
        }
        setLocations(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load locations');
      } finally {
        setIsLoading(false);
      }
    };

    loadLocations();
  }, [district]);

  return { locations, isLoading, error };
}

export function useProductCategories(mainCategory?: string) {
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoading(true);
        setError(null);
        let data;
        if (mainCategory) {
          data = await referenceDataSyncService.getCategoriesByMainCategory(mainCategory);
        } else {
          data = await referenceDataSyncService.getProductCategories();
        }
        setCategories(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load categories');
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, [mainCategory]);

  return { categories, isLoading, error };
}
